// Turns whatever a social field holds into something safe to render. These values are typed and
// pasted by people, so one column mixes bare handles ("politicaltaters", "@politicaltaters") with
// full profile URLs, and the URLs often carry whatever tracking parameters came with the copy --
// LinkedIn's own "?originalSubdomain=uk", a Google Analytics "?_gl=...", a Facebook "?fbclid=...".
//
// The rule that matters: strip the query string BEFORE looking for the handle. A profile URL
// usually ends with a slash, so the last "/"-separated piece of the raw value is the query string
// rather than the handle -- which is how a link came to read "?_gl=1*1dlzfh6*..." and point at
// linkedin.com/in/ with no profile at all.
//
// Returns { url, handle }. `handle` is null when nothing handle-shaped can be found, and the
// caller then shows the icon on its own rather than printing something meaningless.

// Deliberately conservative: what remains of a real handle. Anything with a "?", "=", "&", a
// space or a slash in it is a fragment of a URL we failed to take apart, not a handle.
const HANDLE_PATTERN = /^[A-Za-z0-9._-]+$/;

const looksLikeUrl = (value) => /^(https?:)?\/\//i.test(value) || /^[\w-]+(\.[\w-]+)+\//.test(value);

// The last non-empty path segment, so a trailing slash doesn't win.
const lastPathSegment = (path) => {
  const segments = path.split('/').filter((segment) => segment.length > 0);
  return segments.length > 0 ? segments[segments.length - 1] : null;
};

// Path words the platforms route on. They are handle-shaped, so a URL that stops before the
// profile itself -- "linkedin.com/in/" once its query string is stripped -- would otherwise
// report "in" as the handle.
const LINKEDIN_ROUTE_WORDS = ['in', 'company', 'pub', 'school', 'showcase', 'groups'];
const X_ROUTE_WORDS = ['i', 'home', 'intent', 'share', 'search', 'hashtag'];

function parseSocial(rawValue, { profileBaseUrl, routeWords }) {
  const value = (rawValue || '').trim();
  if (!value) return { url: null, handle: null };

  if (looksLikeUrl(value)) {
    // Keep the URL the person actually stored (minus the query and fragment) rather than
    // rebuilding one from the handle: the path may be a form this function does not model, such
    // as a LinkedIn /company/ or /pub/ URL, and rebuilding those as /in/ would send the visitor
    // to the wrong page -- or to no page.
    const withoutQuery = value.split(/[?#]/)[0];
    const withProtocol = /^https?:/i.test(withoutQuery) ? withoutQuery : `https://${withoutQuery.replace(/^\/\//, '')}`;

    let path;
    try {
      path = new URL(withProtocol).pathname;
    } catch {
      return { url: withProtocol, handle: null };
    }

    const segment = lastPathSegment(path);
    const usable = segment && HANDLE_PATTERN.test(segment) && !routeWords.includes(segment.toLowerCase());
    return { url: withProtocol, handle: usable ? segment : null };
  }

  // Not a URL, so the value is the handle itself. A leading "@" is how people write one; it is
  // never part of the handle in the URL.
  const handle = value.replace(/^@/, '');
  if (!HANDLE_PATTERN.test(handle)) return { url: null, handle: null };

  return { url: `${profileBaseUrl}${handle}`, handle };
}

// Handles run long -- "jessica-dello-buono-b5ab4123" is 28 characters -- and the column they sit
// in is one line tall and about 190px wide, next to an icon. Shortening the text here rather than
// letting CSS clip it means the cut is marked, and the whole handle stays available on hover
// through the caller's title attribute. The limit is what fits that line: measured, not guessed.
const HANDLE_DISPLAY_LIMIT = 16;

export const truncateHandle = (handle) => (
  handle && handle.length > HANDLE_DISPLAY_LIMIT ? `${handle.slice(0, HANDLE_DISPLAY_LIMIT - 1)}…` : handle
);

export const parseLinkedIn = (value) => parseSocial(value, {
  profileBaseUrl: 'https://www.linkedin.com/in/',
  routeWords: LINKEDIN_ROUTE_WORDS,
});

export const parseX = (value) => parseSocial(value, {
  profileBaseUrl: 'https://x.com/',
  routeWords: X_ROUTE_WORDS,
});

export default { parseLinkedIn, parseX, truncateHandle };
